import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ProcessShowcase from "@/components/ProcessShowcase";
import { SattlereiScene, SATTLEREI_STEPS } from "@/components/processScenes";
import { WORKSHOP_VIDEOS } from "@/data/workshopVideos";
import SeoHead from "@/components/SeoHead";
import AutocompleteInput from "@/components/AutocompleteInput";
import MultiPhotoUpload from "@/components/MultiPhotoUpload";
import { searchBrands, searchModels } from "@/data/vehicles";
import { SITE_URL } from "@/seo/data";
import api from "@/lib/api";
import { isValidEmail } from "@/lib/validate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Car, CalendarCheck, UserCircle, CheckCircle, CaretDown, ArrowRight, MapPin,
  Armchair, CurrencyEur, ShieldCheck, Warning, Scissors, Sun, Wrench,
} from "@phosphor-icons/react";

const YEARS = Array.from({ length: 40 }, (_, i) => String(new Date().getFullYear() - i));
const STEPS = ["Leistung", "Fahrzeug", "Details", "Preis", "Termin", "Bezahlen"];

const SERVICES = [
  {
    key: "himmel",
    title: "Autohimmel",
    desc: "Der Stoff Ihres Dachhimmels hängt herunter, löst sich oder soll komplett erneuert werden.",
    cta: "Autohimmel auswählen",
    icon: Sun,
  },
  {
    key: "heckscheibe",
    title: "Cabrio-Heckscheibe",
    desc: "Die Glas- oder Kunststoff-Heckscheibe Ihres Cabrioverdecks löst sich vom Verdeck.",
    cta: "Heckscheibe auswählen",
    icon: Car,
  },
  {
    key: "verdeck",
    title: "Cabrioverdeck",
    desc: "Ihr Cabrioverdeck ist beschädigt, undicht, verschlissen oder muss komplett erneuert werden.",
    cta: "Cabrioverdeck auswählen",
    icon: Scissors,
  },
  {
    key: "fahrersitz",
    title: "Fahrersitz",
    desc: "Sitzfläche abgenutzt, Naht aufgegangen, Loch oder Riss im Bezug — wir reparieren Ihren Sitz.",
    cta: "Fahrersitz auswählen",
    icon: Armchair,
  },
  {
    key: "sitzwangen",
    title: "Sitzwangen",
    desc: "Die Seitenwange ist abgewetzt oder durchgesessen? Wir erneuern Bezug und Polster gezielt.",
    cta: "Sitzwangen auswählen",
    icon: Wrench,
  },
];

const BODY_CLASSES = [
  ["kleinwagen", "Kleinwagen"], ["limousine", "Limousine"], ["coupe", "Coupé"],
  ["kombi", "Kombi"], ["suv", "SUV"], ["van", "Van / Transporter"], ["sonstiges", "Sonstiges"],
];
const ROOFS = [["normal", "Normales Dach"], ["schiebedach", "Schiebedach"], ["panoramadach", "Panoramadach"]];
const HIMMEL_ZUSTAND = [
  ["haengt_teilweise", "Stoff hängt teilweise"], ["haengt_komplett", "Stoff hängt fast vollständig"],
  ["beschaedigt", "Stoff beschädigt"], ["neu_beziehen", "Himmel soll komplett neu bezogen werden"],
  ["sonstiges", "Sonstiger Schaden"],
];
const REAR_TYPES = [["glas", "Glasheckscheibe"], ["kunststoff", "Kunststoffheckscheibe"], ["unbekannt", "Weiß ich nicht"]];
const DETACHMENTS = [
  ["klein", "Kleine Stelle / bis ca. 20 cm"], ["mittel", "20–50 cm"], ["gross", "50–100 cm"],
  ["sehr_gross", "Mehr als 100 cm"], ["komplett", "Fast komplett gelöst"],
];
const VERDECK_DAMAGES = [
  ["stoff", "Verdeckstoff beschädigt"], ["gerissen", "Verdeck gerissen"], ["undicht", "Verdeck undicht"],
  ["naehte", "Nähte beschädigt"], ["innenhimmel", "Innenhimmel beschädigt"], ["heckscheibe", "Heckscheibe beschädigt"],
  ["mechanik", "Mechanik / Gestänge"], ["komplett", "Komplettes Verdeck erneuern"], ["sonstiges", "Sonstiger Schaden"],
];

const SEAT_MATERIALS = [
  ["stoff", "Stoff"], ["leder", "Leder"], ["kunstleder", "Kunstleder"],
  ["alcantara", "Alcantara / Mikrofaser"], ["unbekannt", "Weiß ich nicht"],
];
const SEAT_DAMAGES = [
  ["sitzflaeche", "Sitzfläche abgenutzt / beschädigt"], ["seitenwange", "Seitenwange durchgesessen / abgewetzt"],
  ["naht", "Naht aufgegangen"], ["loch_riss", "Loch oder Riss"],
  ["polster", "Polster / Schaumstoff defekt"], ["verfaerbung", "Verfärbung / Abrieb"],
  ["sonstiges", "Sonstiger Schaden"],
];

const HIMMEL_INCLUDED = [
  "Ausbau des Dachhimmels", "Entfernung des alten Stoffes", "Vorbereitung und Reinigung",
  "Neuer Himmelstoff", "Professionelle Verklebung", "Wiedereinbau des Dachhimmels",
];

const BENEFITS = [
  "Preis direkt online", "Termin online buchen", "Online bezahlen",
  "Fachgerechte Reparatur", "Standort Ludwigshafen", "Transparente Preise",
];

const TRUST = [
  { icon: ShieldCheck, t: "Sorgfalt vom Ausbau bis zum Einbau", d: "Sattlerei-Arbeiten werden bei uns fachgerecht und mit Sorgfalt ausgeführt — vom Ausbau bis zum Wiedereinbau." },
  { icon: CurrencyEur, t: "Transparente Preise statt Überraschungen", d: "Bei vielen Leistungen berechnen Sie den Preis vorab online. Bei aufwendigeren Reparaturen — z. B. am Cabrioverdeck — prüfen wir zuerst den Zustand und informieren Sie über die voraussichtlichen Kosten. Zusätzliche kostenpflichtige Arbeiten stimmen wir vor der Durchführung mit Ihnen ab." },
  { icon: Wrench, t: "Professionelle Verarbeitung", d: "Saubere Verklebung, ausreichende Trocknungszeit und Endkontrolle gehören bei uns zum Standard." },
  { icon: Armchair, t: "Hochwertige Materialien", d: "Wir verwenden hochwertige Himmelstoffe und Klebstoffe für ein langlebiges Ergebnis." },
  { icon: CalendarCheck, t: "Termin online buchbar", d: "Abgabetermin direkt online auswählen und verbindlich reservieren." },
  { icon: MapPin, t: "Standort Ludwigshafen", d: "Frankenthaler Straße 98, 67059 Ludwigshafen am Rhein — verkehrsgünstig für die ganze Rhein-Neckar-Region." },
];

const FAQS = [
  { q: "Wie lange bleibt mein Fahrzeug bei Ihnen?", a: "Für Sattlerei-Arbeiten benötigen wir das Fahrzeug in der Regel mindestens drei Werktage." },
  { q: "Muss ich vorher einen Termin vereinbaren?", a: "Ja. Sattlerei-Arbeiten werden ausschließlich nach Termin durchgeführt." },
  { q: "Kann ich den Preis vorher sehen?", a: "Bei vielen Leistungen wie Autohimmel und Cabrio-Heckscheiben erhalten Sie bereits online eine Preisberechnung. Bei umfangreichen Cabrioverdeck-Arbeiten prüfen wir zunächst Fahrzeugdaten und Fotos." },
  { q: "Warum muss mein Fahrzeug mehrere Tage bleiben?", a: "Viele Sattlerei-Arbeiten benötigen Ausbau, Vorbereitung, Verklebung, Verarbeitung sowie ausreichende Trocknungs- und Aushärtungszeit." },
  { q: "Wo wird die Reparatur durchgeführt?", a: "Unsere Sattlerei-Arbeiten werden nach vorheriger Terminvereinbarung an unserem Werkstattstandort in der Frankenthaler Straße 98, 67059 Ludwigshafen am Rhein, durchgeführt, soweit die jeweilige Leistung dort angeboten wird. Auch Kunden aus Mannheim, Frankenthal, Speyer, Heidelberg und der Umgebung können bei uns anfragen. Den genauen Ablauf und die voraussichtliche Bearbeitungszeit besprechen wir vor dem Termin." },
  { q: "Was kostet es, den Autohimmel neu beziehen zu lassen?", a: "Das Neubeziehen des Dachhimmels kostet je nach Fahrzeug zwischen 449 € (Kleinwagen) und 699 € (Van/Transporter) inkl. Material, Montage und MwSt. Für Schiebedach oder Panoramadach fällt ein kleiner Aufpreis an. Ihren exakten Festpreis sehen Sie direkt online im Konfigurator." },
  { q: "Was kostet es, eine Cabrio-Heckscheibe neu zu verkleben?", a: "Je nachdem, wie stark sich die Heckscheibe vom Verdeck gelöst hat, liegt der Festpreis zwischen 399 € und 799 € inkl. MwSt. Nach Ihrem Foto-Upload prüfen wir vor dem Termin, ob die Verklebung technisch möglich ist." },
  { q: "Kann eine gelöste Cabrio-Heckscheibe repariert werden, oder brauche ich ein neues Verdeck?", a: "In vielen Fällen lässt sich eine gelöste Glas- oder Kunststoff-Heckscheibe wieder fachgerecht mit dem Verdeck verkleben — ein komplett neues Cabrioverdeck ist dann nicht nötig. Wichtig: Verdeck möglichst nicht mehr öffnen oder schließen, damit sich der Schaden nicht vergrößert." },
  { q: "Für welche Fahrzeuge bieten Sie Sattlerei-Arbeiten an?", a: "Wir arbeiten an nahezu allen Pkw — vom Kleinwagen über Limousine, Kombi und SUV bis zum Transporter, inklusive Cabrios sowie Youngtimern und Oldtimern. Wählen Sie einfach Ihr Fahrzeug im Konfigurator; bei Sonderfällen beraten wir Sie gerne telefonisch." },
  { q: "Warum lösen sich Stoffe und Verkleidungen im Auto?", a: "Häufig sind Alterung, Wärme, Feuchtigkeit, Schaumstoffzerfall oder geschwächte Klebeverbindungen verantwortlich. Besonders die dünne Schaumstoffschicht unter vielen Bezügen zersetzt sich mit den Jahren — dann verliert der Stoff seinen Halt." },
  { q: "Sind solche Schäden immer Herstellerfehler?", a: "Nein. Viele Schäden entstehen durch normale Materialalterung. Herstellungs- oder Verarbeitungsfehler können im Einzelfall eine Rolle spielen, müssen aber gesondert anhand des Materialzustands beurteilt werden." },
  { q: "Kann man jeden Schaden reparieren?", a: "Nein. Die Reparaturmöglichkeit hängt vom Material, der Konstruktion und dem Zustand der beschädigten Bauteile ab. Wir prüfen Ihren Fall ehrlich und sagen Ihnen, ob sich eine Reparatur lohnt." },
  { q: "Ist eine Reparatur immer günstiger als ein Austausch?", a: "Nicht unbedingt. Bei stark beschädigten Materialien kann ein Austausch langfristig sinnvoller sein. Wir beraten Sie transparent zu beiden Möglichkeiten." },
  { q: "Warum sollte man Schäden frühzeitig beheben lassen?", a: "Kleine Schäden können sich durch weitere Nutzung, Feuchtigkeit oder mechanische Belastung vergrößern. Eine frühzeitige Reparatur ist meist einfacher und günstiger." },
  { q: "Wie kann man die Lebensdauer von Fahrzeugmaterialien verlängern?", a: "Durch geeignete Pflege, schonende Reinigung, Schutz vor extremen Belastungen und frühzeitige Behandlung von Schäden. Ausführliche Pflege-Tipps finden Sie in unseren Sattlerei-Ratgebern." },
];

const RATGEBER_LINKS = [
  { to: "/sattlerei/dachhimmel-neu-beziehen", t: "Dachhimmel löst sich?", d: "Warum der Autohimmel herunterhängt, warum Sprühkleber nicht hält und wie die professionelle Erneuerung abläuft.", tag: "ab 449 € buchbar" },
  { to: "/sattlerei/cabrio-heckscheibe-kleben", t: "Cabrio-Heckscheibe löst sich?", d: "Ursachen, warum Sie das Verdeck nicht mehr öffnen sollten und wann eine Neuverklebung möglich ist.", tag: "ab 399 € buchbar" },
  { to: "/sattlerei/cabrioverdeck-reparieren", t: "Cabrioverdeck undicht oder gerissen?", d: "Scheuerstellen, offene Nähte, Wassereintritt: die häufigsten Ursachen und wann sich eine Reparatur lohnt.", tag: "Online anfragen" },
  { to: "/sattlerei/fahrersitz-reparieren", t: "Fahrersitz abgenutzt?", d: "Sitzfläche, Nähte oder Polster beschädigt: warum gerade der Fahrersitz leidet und wie die Reparatur abläuft.", tag: "ab 189 € anfragen" },
  { to: "/sattlerei/sitzwangen-reparieren", t: "Sitzwange durchgesessen?", d: "Die Seitenwange ist die meistbelastete Stelle des Sitzes — so wird sie gezielt erneuert statt den Sitz zu tauschen.", tag: "ab 149 € anfragen" },
  { to: "/sattlerei/ledersitze-pflege", t: "Ledersitze: Risse, Falten & Nähte", d: "Warum Leder reißt, Sitzwangen verschleißen und Nähte aufgehen — mit Pflege-Tipps vom Fachbetrieb.", tag: "Ratgeber" },
  { to: "/sattlerei/innenraum-pflege", t: "Innenraum richtig pflegen", d: "Türverkleidungen, Alcantara und die wichtigsten Pflege-Empfehlungen für die gesamte Innenausstattung.", tag: "Ratgeber" },
];

const GREEN = "bg-[#55C96B] hover:bg-[#249143]";

const fmtDate = (d) => {
  if (!d) return "";
  const [y, m, dd] = d.split("-");
  return new Date(Number(y), Number(m) - 1, Number(dd), 12).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};
const eur = (n) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });

const Pill = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors ${active ? "bg-[#55C96B] text-white border-[#55C96B]" : "bg-white border-slate-200 hover:border-[#55C96B] text-slate-700"}`}
  >
    {children}
  </button>
);

export default function Sattlerei() {
  const [step, setStep] = useState(0);
  const [service, setService] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [bodyClass, setBodyClass] = useState("");
  const [roof, setRoof] = useState("");
  const [zustand, setZustand] = useState("");
  const [rearType, setRearType] = useState("");
  const [detachment, setDetachment] = useState("");
  const [damages, setDamages] = useState([]);
  const [seatMaterial, setSeatMaterial] = useState("");
  const [seatDamages, setSeatDamages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [quote, setQuote] = useState(null);
  const [days, setDays] = useState([]);
  const [selDate, setSelDate] = useState("");
  const [selPickup, setSelPickup] = useState("");
  const [selTime, setSelTime] = useState("");
  const [confirmDuration, setConfirmDuration] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", street: "", zip_code: "", city: "", license_plate: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);

  const minPhotos = service === "himmel" ? 0 : 3;

  const goStep = (s) => {
    setStep(s);
    document.getElementById("konfigurator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectService = (key) => {
    setService(key);
    setQuote(null);
    setPhotos([]);
    setSeatMaterial("");
    setSeatDamages([]);
    goStep(1);
  };

  useEffect(() => {
    const key = new URLSearchParams(window.location.search).get("service");
    if (key && SERVICES.some((s) => s.key === key)) {
      selectService(key);
      setTimeout(() => document.getElementById("konfigurator")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vehicleNext = () => {
    if (!make.trim() || !model.trim()) return toast.error("Bitte Hersteller und Modell angeben");
    goStep(2);
  };

  const detailsValid = () => {
    if (service === "himmel") {
      if (!bodyClass) return "Bitte Karosserieform wählen";
      if (!roof) return "Bitte Dachvariante wählen";
      if (!zustand) return "Bitte den Zustand des Himmels angeben";
    }
    if (service === "heckscheibe") {
      if (!rearType) return "Bitte Heckscheiben-Typ wählen";
      if (!detachment) return "Bitte angeben, wie stark sich die Scheibe gelöst hat";
      if (photos.length < 3) return "Bitte mindestens 3 Fotos hochladen";
    }
    if (service === "verdeck") {
      if (damages.length === 0) return "Bitte mindestens einen Schaden auswählen";
      if (photos.length < 3) return "Bitte mindestens 3 Fotos hochladen";
    }
    if (service === "fahrersitz" || service === "sitzwangen") {
      if (!seatMaterial) return "Bitte das Material des Sitzes wählen";
      if (seatDamages.length === 0) return "Bitte mindestens einen Schaden auswählen";
      if (photos.length < 3) return "Bitte mindestens 3 Fotos hochladen";
    }
    return null;
  };

  const fetchQuote = async () => {
    const err = detailsValid();
    if (err) return toast.error(err);
    try {
      const { data } = await api.post("/sattlerei/quote", {
        service, body_class: bodyClass || undefined, roof: roof || undefined, detachment: detachment || undefined,
      });
      setQuote(data);
      goStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preis konnte nicht berechnet werden");
    }
  };

  const loadAvailability = async () => {
    try {
      const { data } = await api.get("/sattlerei/availability");
      setDays(data.days || []);
      goStep(4);
    } catch {
      toast.error("Termine konnten nicht geladen werden");
    }
  };

  const terminNext = () => {
    if (!selDate || !selTime) return toast.error("Bitte Abgabetermin und Uhrzeit wählen");
    if (!confirmDuration) return toast.error("Bitte bestätigen Sie den Mindestaufenthalt von 3 Werktagen");
    goStep(5);
  };

  const submitBooking = async (payMode) => {
    for (const k of ["first_name", "last_name", "phone", "email", "street", "zip_code", "city"]) {
      if (!form[k].trim()) return toast.error("Bitte alle Pflichtfelder ausfüllen");
    }
    if (!isValidEmail(form.email)) return toast.error("Bitte eine gültige E-Mail-Adresse angeben (z.B. max@beispiel.de)");
    setSubmitting(true);
    try {
      const { data } = await api.post("/sattlerei/bookings", {
        service, manufacturer: make, model, year,
        body_class: bodyClass || undefined, roof: roof || undefined, himmel_zustand: zustand || undefined,
        rear_window_type: rearType || undefined, detachment: detachment || undefined,
        verdeck_damage: damages.length ? damages : undefined,
        seat_material: seatMaterial || undefined,
        seat_damage: seatDamages.length ? seatDamages : undefined,
        photos: photos.map((p) => p.file_id),
        date: selDate, time: selTime,
        ...form, email: form.email.trim().toLowerCase(), confirm_duration: true,
      });
      setBooking(data);
      if (payMode && data.price) {
        try {
          const { data: pay } = await api.post(`/sattlerei/bookings/${data.id}/checkout`, {
            origin_url: window.location.origin, mode: payMode,
          });
          window.location.href = pay.url;
          return;
        } catch {
          toast.error("Zahlung konnte nicht gestartet werden — Ihre Buchung ist trotzdem reserviert.");
        }
      }
      goStep(6);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Die Buchung konnte gerade nicht abgeschlossen werden. Bitte versuchen Sie es erneut.");
      if (err?.response?.status === 409) {
        loadAvailability();
        setSelDate(""); setSelTime("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const priceStr = quote?.price ? `${eur(quote.price)} €` : "";
  const depositStr = quote?.deposit ? `${eur(quote.deposit)} €` : "";
  const restStr = quote?.price ? `${eur(quote.price - quote.deposit)} €` : "";

  const schema = useMemo(() => ([
    {
      "@context": "https://schema.org", "@type": "Service",
      name: "Autosattlerei — Autohimmel, Cabrioverdeck & Cabrio-Heckscheibe",
      serviceType: "Autosattlerei",
      provider: { "@type": "AutoRepair", name: "Servus Autoglas — AutoGlas Erol GmbH", address: { "@type": "PostalAddress", streetAddress: "Frankenthaler Str. 98", postalCode: "67059", addressLocality: "Ludwigshafen am Rhein", addressCountry: "DE" }, telephone: "+49 621 527429" },
      areaServed: [{ "@type": "City", name: "Ludwigshafen" }, { "@type": "City", name: "Mannheim" }, { "@type": "City", name: "Frankenthal" }, { "@type": "City", name: "Heidelberg" }],
      offers: { "@type": "AggregateOffer", priceCurrency: "EUR", lowPrice: "399", description: "Festpreise für Autohimmel und Cabrio-Heckscheiben, Preisindikation für Cabrioverdecke." },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Sattlerei-Leistungen mit Online-Preis",
        itemListElement: [
          { "@type": "Offer", name: "Autohimmel neu beziehen (Dachhimmel-Reparatur)", priceSpecification: { "@type": "PriceSpecification", minPrice: "449", maxPrice: "699", priceCurrency: "EUR" } },
          { "@type": "Offer", name: "Cabrio-Heckscheibe neu verkleben", priceSpecification: { "@type": "PriceSpecification", minPrice: "399", maxPrice: "799", priceCurrency: "EUR" } },
          { "@type": "Offer", name: "Cabrioverdeck reparieren / erneuern", priceSpecification: { "@type": "PriceSpecification", minPrice: "1490", priceCurrency: "EUR" } },
        ],
      },
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Sattlerei", item: SITE_URL + "/sattlerei" },
      ],
    },
  ]), []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead
        title="Autosattlerei Ludwigshafen: Autohimmel ab 449 € & Cabrioverdeck | Servus Autoglas"
        description="Autosattlerei in Ludwigshafen: Autohimmel neu beziehen ab 449 €, Cabrio-Heckscheibe kleben ab 399 €, Cabrioverdeck reparieren. Preis online berechnen & Termin direkt buchen — Mannheim, Ludwigshafen & Rhein-Neckar."
        canonical={`${SITE_URL}/sattlerei`}
      />
      <Helmet>
        {schema.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>
      <Header />

      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EAF8ED] border border-[#cdeed6] text-[#249143] text-xs font-bold uppercase tracking-widest">
            <Armchair size={14} weight="fill" /> Neue Abteilung · Sattlerei
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Autosattlerei Ludwigshafen
          </h1>
          <p className="mt-4 text-slate-600 text-lg max-w-2xl">
            Autohimmel, Cabrioverdecke und Cabrio-Heckscheiben professionell reparieren lassen.
            Fahrzeug auswählen, Preis berechnen und Termin direkt online buchen.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {BENEFITS.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF8ED] text-[#249143] text-sm font-medium">
                <CheckCircle size={15} weight="fill" /> {b}
              </span>
            ))}
          </div>
          <Button
            onClick={() => document.getElementById("konfigurator")?.scrollIntoView({ behavior: "smooth" })}
            className={`mt-7 h-13 h-12 px-8 ${GREEN} text-white font-semibold rounded-xl`}
            data-testid="sattlerei-hero-cta"
          >
            Jetzt Preis berechnen <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* KONFIGURATOR */}
      <section id="konfigurator" className="py-10 md:py-14 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1" data-testid="sattlerei-stepper">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${i === Math.min(step, 5) ? "bg-[#55C96B] text-white" : i < step ? "bg-[#EAF8ED] text-[#249143]" : "bg-slate-100 text-slate-400"}`}>
                  <span>{i + 1}</span><span className="hidden sm:inline">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`w-4 h-0.5 ${i < step ? "bg-[#55C96B]" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          <div className="mt-6 bg-white rounded-3xl border border-slate-200 p-6 md:p-8" data-testid="sattlerei-configurator">
            {/* SCHRITT 1: LEISTUNG */}
            {step === 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-900">Was möchten Sie reparieren lassen?</h2>
                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  {SERVICES.map(({ key, title, desc, cta, icon: Icon }) => (
                    <div key={key} className="rounded-2xl border border-slate-200 hover:border-[#55C96B] transition-colors p-5 flex flex-col">
                      <div className="w-11 h-11 rounded-xl bg-[#EAF8ED] text-[#249143] flex items-center justify-center">
                        <Icon size={24} weight="duotone" />
                      </div>
                      <h3 className="mt-3 font-bold text-slate-900 text-lg">{title}</h3>
                      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed flex-1">{desc}</p>
                      <Button onClick={() => selectService(key)} className={`mt-4 h-11 w-full ${GREEN} text-white font-semibold rounded-xl`} data-testid={`sattlerei-service-${key}`}>
                        {cta}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SCHRITT 2: FAHRZEUG */}
            {step === 1 && (
              <div className="space-y-5 max-w-xl">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Car size={24} className="text-[#249143]" /> Ihr Fahrzeug</h2>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Hersteller *</label>
                  <AutocompleteInput value={make} onChange={setMake} getSuggestions={(q) => searchBrands(q, 10)} placeholder="z.B. BMW, Volkswagen, Mercedes-Benz" testId="sattlerei-make" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Modell *</label>
                  <AutocompleteInput value={model} onChange={setModel} getSuggestions={(q) => searchModels(make, q, 12)} placeholder="z.B. 3er, Golf, C-Klasse" testId="sattlerei-model" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Baujahr</label>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sattlerei-year">
                    <option value="">Bitte wählen</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="flex gap-3 items-center">
                  <button onClick={() => goStep(0)} className="text-sm text-slate-500 underline">← Zurück</button>
                  <Button onClick={vehicleNext} className={`ml-auto h-12 px-8 ${GREEN} text-white font-semibold rounded-xl`} data-testid="sattlerei-vehicle-next">
                    Weiter <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT 3: DETAILS */}
            {step === 2 && (
              <div className="space-y-6 max-w-2xl">
                <h2 className="text-xl font-bold text-slate-900">Details zu Ihrer Reparatur</h2>

                {service === "himmel" && (
                  <>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Karosserie *</p>
                      <div className="flex flex-wrap gap-2" data-testid="sattlerei-body-select">
                        {BODY_CLASSES.map(([k, l]) => (
                          <Pill key={k} active={bodyClass === k} onClick={() => setBodyClass(k)} testId={`sattlerei-body-${k}`}>{l}</Pill>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Welche Dachvariante hat das Fahrzeug? *</p>
                      <div className="flex flex-wrap gap-2" data-testid="sattlerei-roof-select">
                        {ROOFS.map(([k, l]) => (
                          <Pill key={k} active={roof === k} onClick={() => setRoof(k)} testId={`sattlerei-roof-${k}`}>{l}</Pill>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Was ist mit dem Himmel? *</p>
                      <div className="grid sm:grid-cols-2 gap-2" data-testid="sattlerei-zustand-select">
                        {HIMMEL_ZUSTAND.map(([k, l]) => (
                          <Pill key={k} active={zustand === k} onClick={() => setZustand(k)} testId={`sattlerei-zustand-${k}`}>{l}</Pill>
                        ))}
                      </div>
                    </div>
                    <MultiPhotoUpload
                      photos={photos} onChange={setPhotos} min={0} max={5}
                      label="Fotos hochladen (optional)"
                      hint="Bis zu 5 Bilder — JPG, PNG oder HEIC, direkt mit der Handykamera möglich."
                      accentClass="text-[#249143]" testid="sattlerei-photos"
                    />
                  </>
                )}

                {service === "heckscheibe" && (
                  <>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Welche Heckscheibe haben Sie? *</p>
                      <div className="flex flex-wrap gap-2" data-testid="sattlerei-reartype-select">
                        {REAR_TYPES.map(([k, l]) => (
                          <Pill key={k} active={rearType === k} onClick={() => setRearType(k)} testId={`sattlerei-reartype-${k}`}>{l}</Pill>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Wie stark hat sich die Scheibe gelöst? *</p>
                      <div className="grid sm:grid-cols-2 gap-2" data-testid="sattlerei-detachment-select">
                        {DETACHMENTS.map(([k, l]) => (
                          <Pill key={k} active={detachment === k} onClick={() => setDetachment(k)} testId={`sattlerei-detachment-${k}`}>{l}</Pill>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3" data-testid="sattlerei-cabrio-warning">
                      <Warning size={22} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-slate-900 text-sm">Wichtiger Hinweis</p>
                        <p className="text-sm text-slate-700 mt-0.5">Bitte das Cabrioverdeck möglichst nicht mehr öffnen oder schließen, wenn sich die Heckscheibe bereits vom Verdeck gelöst hat. Dadurch kann sich der Schaden vergrößern.</p>
                      </div>
                    </div>
                    <MultiPhotoUpload
                      photos={photos} onChange={setPhotos} min={3} max={8}
                      label="Fotos hochladen"
                      hint="Bitte laden Sie Fotos der gesamten Heckscheibe sowie Nahaufnahmen der gelösten Stellen hoch. So können wir den Auftrag vor Ihrem Termin prüfen."
                      accentClass="text-[#249143]" testid="sattlerei-photos"
                    />
                  </>
                )}

                {service === "verdeck" && (
                  <>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Was ist beschädigt? (Mehrfachauswahl möglich) *</p>
                      <div className="grid sm:grid-cols-2 gap-2" data-testid="sattlerei-damage-select">
                        {VERDECK_DAMAGES.map(([k, l]) => (
                          <Pill key={k} active={damages.includes(k)} onClick={() => setDamages(damages.includes(k) ? damages.filter((x) => x !== k) : [...damages, k])} testId={`sattlerei-damage-${k}`}>
                            {l}
                          </Pill>
                        ))}
                      </div>
                    </div>
                    <MultiPhotoUpload
                      photos={photos} onChange={setPhotos} min={3} max={8}
                      label="Fotos hochladen"
                      hint="Bitte laden Sie Fotos des gesamten Verdecks sowie Nahaufnahmen der beschädigten Stellen hoch."
                      accentClass="text-[#249143]" testid="sattlerei-photos"
                    />
                  </>
                )}

                {(service === "fahrersitz" || service === "sitzwangen") && (
                  <>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Welches Material hat der Sitz? *</p>
                      <div className="flex flex-wrap gap-2" data-testid="sattlerei-seatmaterial-select">
                        {SEAT_MATERIALS.map(([k, l]) => (
                          <Pill key={k} active={seatMaterial === k} onClick={() => setSeatMaterial(k)} testId={`sattlerei-seatmaterial-${k}`}>{l}</Pill>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Was ist beschädigt? (Mehrfachauswahl möglich) *</p>
                      <div className="grid sm:grid-cols-2 gap-2" data-testid="sattlerei-seatdamage-select">
                        {SEAT_DAMAGES.map(([k, l]) => (
                          <Pill key={k} active={seatDamages.includes(k)} onClick={() => setSeatDamages(seatDamages.includes(k) ? seatDamages.filter((x) => x !== k) : [...seatDamages, k])} testId={`sattlerei-seatdamage-${k}`}>
                            {l}
                          </Pill>
                        ))}
                      </div>
                    </div>
                    <MultiPhotoUpload
                      photos={photos} onChange={setPhotos} min={3} max={8}
                      label="Fotos hochladen"
                      hint="Bitte laden Sie Fotos des gesamten Sitzes sowie Nahaufnahmen der beschädigten Stellen hoch. So können wir Ihnen den Preis verbindlich bestätigen."
                      accentClass="text-[#249143]" testid="sattlerei-photos"
                    />
                  </>
                )}

                <div className="flex gap-3 items-center">
                  <button onClick={() => goStep(1)} className="text-sm text-slate-500 underline">← Zurück</button>
                  <Button onClick={fetchQuote} className={`ml-auto h-12 px-8 ${GREEN} text-white font-semibold rounded-xl`} data-testid="sattlerei-quote-button">
                    Preis berechnen <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT 4: PREIS */}
            {step === 3 && quote && (
              <div className="space-y-5">
                <div className="rounded-2xl bg-white border-2 border-[#a9e3b6] p-6 md:p-8 max-w-xl" data-testid="sattlerei-price-box">
                  <p className="text-xs font-bold text-[#249143] uppercase tracking-widest">{quote.service_label}</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">{make} {model}{year ? ` (${year})` : ""}</h2>
                  {quote.price_type === "fixed" ? (
                    <>
                      <p className="mt-4 text-sm font-semibold text-slate-500">Ihr Preis</p>
                      <div className="flex items-end gap-2">
                        <span className="text-5xl font-bold text-[#249143]" data-testid="sattlerei-price">{priceStr}</span>
                        <span className="text-slate-500 font-semibold mb-1.5">inkl. MwSt.</span>
                      </div>
                      {service === "himmel" && (
                        <>
                          <p className="mt-5 text-sm font-semibold text-slate-700">Im Preis enthalten:</p>
                          <ul className="mt-2 space-y-2">
                            {HIMMEL_INCLUDED.map((t) => (
                              <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle size={18} weight="fill" className="text-[#55C96B] mt-0.5 flex-shrink-0" />{t}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      {service === "heckscheibe" && (
                        <p className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-3" data-testid="sattlerei-photo-caveat">
                          Preis vorbehaltlich der Prüfung Ihrer hochgeladenen Fotos. Sollten wir feststellen, dass eine
                          Reparatur technisch nicht möglich ist, kontaktieren wir Sie vor Beginn der Arbeiten.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="mt-4 text-sm font-semibold text-slate-500">Preisindikation</p>
                      <div className="flex items-end gap-2">
                        <span className="text-5xl font-bold text-[#249143]" data-testid="sattlerei-price">ab {eur(quote.price_from)} €</span>
                      </div>
                      <p className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-3">
                        Nach Prüfung Ihrer Fahrzeugdaten und Fotos bestätigen wir Ihnen den endgültigen Preis.
                      </p>
                    </>
                  )}
                  <Button onClick={loadAvailability} className={`mt-6 w-full h-12 ${GREEN} text-white font-semibold rounded-xl`} data-testid="sattlerei-to-calendar">
                    Termin auswählen <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
                <button onClick={() => goStep(2)} className="text-sm text-slate-500 underline">← Details ändern</button>
              </div>
            )}

            {/* SCHRITT 5: TERMIN */}
            {step === 4 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><CalendarCheck size={24} className="text-[#249143]" /> Abgabetermin wählen</h2>
                <p className="text-sm text-slate-600">
                  Für Ausbau, Vorbereitung, Verarbeitung, Trocknung bzw. Aushärtung und Wiedereinbau benötigen wir Ihr
                  Fahrzeug <strong>mindestens drei Werktage</strong>.
                </p>
                {days.filter((d) => d.free).length === 0 ? (
                  <p className="text-slate-500">Aktuell sind keine freien Abgabetermine verfügbar. Bitte kontaktieren Sie uns telefonisch.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="sattlerei-days">
                    {days.map((d) => (
                      <div key={d.date} className={`rounded-2xl border p-4 bg-white ${!d.free ? "opacity-50" : selDate === d.date ? "border-[#55C96B] ring-1 ring-[#a9e3b6]" : "border-slate-200"}`}>
                        <button
                          onClick={() => { if (d.free) { setSelDate(d.date); setSelPickup(d.pickup_date); setSelTime(""); } }}
                          disabled={!d.free}
                          className="w-full text-left disabled:cursor-not-allowed"
                          data-testid={`sattlerei-day-${d.date}`}
                        >
                          <span className="font-semibold text-slate-900">{fmtDate(d.date)}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            {d.free ? <>Voraussichtliche Abholung: <strong className="text-[#249143]">{fmtDate(d.pickup_date)}</strong></> : "Belegt"}
                          </span>
                        </button>
                        {selDate === d.date && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {d.slots.map((s) => (
                              <button key={s} onClick={() => setSelTime(s)} data-testid={`sattlerei-slot-${s}`}
                                className={`px-3.5 py-2 rounded-lg text-sm font-semibold border ${selTime === s ? "bg-[#55C96B] text-white border-[#55C96B]" : "bg-white border-slate-200 hover:border-[#55C96B]"}`}>
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer rounded-xl bg-[#EAF8ED] border border-[#cdeed6] p-3.5">
                  <Checkbox checked={confirmDuration} onCheckedChange={setConfirmDuration} className="mt-0.5" data-testid="sattlerei-duration-checkbox" />
                  <span>Mir ist bekannt, dass mein Fahrzeug <strong>mindestens 3 Werktage</strong> in der Werkstatt bleibt.</span>
                </label>
                <div className="flex gap-3 items-center">
                  <button onClick={() => goStep(3)} className="text-sm text-slate-500 underline">← Zurück</button>
                  <Button onClick={terminNext} className={`ml-auto h-12 px-8 ${GREEN} text-white font-semibold rounded-xl`} data-testid="sattlerei-to-form">
                    Weiter <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT 6: DATEN + ZUSAMMENFASSUNG + ZAHLUNG */}
            {step === 5 && quote && (
              <div className="grid lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><UserCircle size={24} className="text-[#249143]" /> Ihre Daten</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-sm font-semibold text-slate-700">Vorname *</label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-11 bg-white" data-testid="sattlerei-first-name" /></div>
                    <div><label className="text-sm font-semibold text-slate-700">Nachname *</label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-11 bg-white" data-testid="sattlerei-last-name" /></div>
                  </div>
                  <div><label className="text-sm font-semibold text-slate-700">Mobiltelefon *</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 bg-white" placeholder="+49 152 …" data-testid="sattlerei-phone" /></div>
                  <div><label className="text-sm font-semibold text-slate-700">E-Mail *</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 bg-white" data-testid="sattlerei-email" /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Straße & Hausnummer *</label><Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} className="h-11 bg-white" data-testid="sattlerei-street" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-sm font-semibold text-slate-700">PLZ *</label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="h-11 bg-white" data-testid="sattlerei-zip" /></div>
                    <div className="col-span-2"><label className="text-sm font-semibold text-slate-700">Ort *</label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-11 bg-white" data-testid="sattlerei-city" /></div>
                  </div>
                  <div><label className="text-sm font-semibold text-slate-700">Kennzeichen</label><Input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} className="h-11 bg-white" placeholder="LU-XX 123" data-testid="sattlerei-plate" /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Nachricht / Bemerkung</label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-white" rows={2} data-testid="sattlerei-note" /></div>
                </div>
                <div>
                  <div className="rounded-2xl bg-white border-2 border-[#a9e3b6] p-6 lg:sticky lg:top-24" data-testid="sattlerei-summary">
                    <p className="text-xs font-bold text-[#249143] uppercase tracking-widest">Ihre Buchung</p>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">Leistung</dt><dd className="font-semibold text-slate-900 text-right">{quote.service_label}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">Fahrzeug</dt><dd className="font-semibold text-slate-900">{make} {model}</dd></div>
                      {year && <div className="flex justify-between gap-3"><dt className="text-slate-500">Baujahr</dt><dd className="font-semibold text-slate-900">{year}</dd></div>}
                      {service === "himmel" && roof && <div className="flex justify-between gap-3"><dt className="text-slate-500">Dach</dt><dd className="font-semibold text-slate-900">{ROOFS.find(([k]) => k === roof)?.[1]}</dd></div>}
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">Preis</dt><dd className="font-bold text-[#249143]">{quote.price ? `${priceStr} inkl. MwSt.` : `ab ${eur(quote.price_from)} € — nach Prüfung`}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">Fahrzeugabgabe</dt><dd className="font-semibold text-slate-900 text-right">{fmtDate(selDate)}<br />{selTime} Uhr</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">Vsl. Abholung</dt><dd className="font-semibold text-slate-900 text-right">{fmtDate(selPickup)}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">Standort</dt><dd className="font-semibold text-slate-900 text-right">Frankenthaler Str. 98,<br />67059 Ludwigshafen</dd></div>
                    </dl>
                    {quote.price ? (
                      <>
                        <Button onClick={() => submitBooking("full")} disabled={submitting} className={`mt-5 w-full h-12 ${GREEN} text-white font-semibold rounded-xl`} data-testid="sattlerei-pay-full">
                          {submitting ? "Wird gebucht…" : `Jetzt ${priceStr} bezahlen`}
                        </Button>
                        <Button onClick={() => submitBooking("deposit")} disabled={submitting} variant="outline" className="mt-2 w-full h-12 rounded-xl border-[#a9e3b6] text-[#249143] hover:bg-[#EAF8ED] font-semibold" data-testid="sattlerei-pay-deposit">
                          Termin mit {depositStr} Anzahlung sichern (25 %)
                        </Button>
                        <p className="mt-2 text-xs text-slate-500 text-center">Bei Anzahlung: Restbetrag {restStr} spätestens bei Fahrzeugabholung. Sichere Zahlung per Kredit-/Debitkarte, Apple Pay oder Google Pay.</p>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => submitBooking(null)} disabled={submitting} className={`mt-5 w-full h-12 ${GREEN} text-white font-semibold rounded-xl`} data-testid="sattlerei-book-request">
                          {submitting ? "Wird gebucht…" : "Verbindlich anfragen & Termin reservieren"}
                        </Button>
                        <p className="mt-2 text-xs text-slate-500 text-center">Nach Prüfung Ihrer Fotos bestätigen wir den endgültigen Preis — erst danach wird eine Zahlung fällig.</p>
                      </>
                    )}
                  </div>
                  <button onClick={() => goStep(4)} className="mt-3 text-sm text-slate-500 underline">← Termin ändern</button>
                </div>
              </div>
            )}

            {/* BESTÄTIGUNG */}
            {step === 6 && booking && (
              <div className="text-center max-w-lg mx-auto py-6" data-testid="sattlerei-success">
                <div className="w-16 h-16 rounded-full bg-[#EAF8ED] text-[#249143] flex items-center justify-center mx-auto">
                  <CheckCircle size={36} weight="fill" />
                </div>
                <h2 className="mt-4 text-2xl font-bold text-slate-900">Vielen Dank für Ihre Buchung!</h2>
                <p className="mt-2 text-slate-600">Ihre Sattlerei-Buchung wurde verbindlich reserviert.</p>
                <div className="mt-5 rounded-2xl bg-white border border-slate-200 p-5 text-left text-sm space-y-1.5">
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Buchungsnummer</span><strong data-testid="sattlerei-booking-number">{booking.booking_number}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Leistung</span><strong className="text-right">{booking.service_label}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Fahrzeug</span><strong>{booking.vehicle?.manufacturer} {booking.vehicle?.model}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Preis</span><strong>{booking.price ? `${eur(booking.price)} € inkl. MwSt.` : `ab ${eur(booking.price_from)} € — nach Prüfung`}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Bezahlt</span><strong>{eur(booking.paid_amount || 0)} €</strong></div>
                  {booking.price ? <div className="flex justify-between gap-3"><span className="text-slate-500">Offener Restbetrag</span><strong>{eur(booking.price - (booking.paid_amount || 0))} €</strong></div> : null}
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Fahrzeugabgabe</span><strong className="text-right">{fmtDate(booking.date)}<br />{booking.time} Uhr</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Vsl. Abholung</span><strong>{fmtDate(booking.pickup_date)}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Standort</span><strong className="text-right">Frankenthaler Str. 98,<br />67059 Ludwigshafen</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Kontakt</span><strong>+49 621 527429</strong></div>
                </div>
                <p className="mt-4 text-sm text-slate-500">Wir haben Ihnen die Bestätigung zusätzlich per E-Mail zugesendet.</p>
                <p className="mt-2 text-sm text-slate-600">
                  Status jederzeit verfolgen:{" "}
                  <Link to={`/status/${booking.booking_number}`} className="text-[#249143] font-semibold underline" data-testid="sattlerei-track-link">
                    {booking.booking_number}
                  </Link>
                </p>
                <Link to="/" className="mt-5 inline-block text-[#249143] font-semibold underline">Zurück zur Startseite</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VERTRAUEN */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Ihr Fahrzeug in guten Händen</h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-3xl">
            Ob sich der Dachhimmel löst, die Heckscheibe Ihres Cabrios nicht mehr richtig hält, das Verdeck
            Schäden aufweist oder Fahrersitz und Sitzwange abgenutzt sind: Bei Servus Autoglas in Ludwigshafen prüfen wir den Zustand Ihres Fahrzeugs und
            erklären Ihnen verständlich, welche Reparaturmöglichkeiten bestehen. So können Sie auf Grundlage
            einer fachlichen Einschätzung entscheiden, welche Lösung für Ihr Fahrzeug sinnvoll ist.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TRUST.map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-5">
                <div className="w-10 h-10 rounded-xl bg-[#EAF8ED] text-[#249143] flex items-center justify-center">
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="mt-3 font-bold text-slate-900">{t}</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARBEITSABLAUF (animiert) + VIDEO */}
      <ProcessShowcase
        eyebrow="So arbeitet unsere Sattlerei"
        title="So entsteht Ihr neuer Dachhimmel — Schritt für Schritt"
        intro="Vom durchhängenden Himmel bis zum straffen Ergebnis: Unser Arbeitsablauf verständlich animiert — so wissen Sie genau, was mit Ihrem Fahrzeug passiert."
        steps={SATTLEREI_STEPS}
        Scene={SattlereiScene}
        accent="#249143"
        soft="#EAF8ED"
        softBorder="#cdeed6"
        videos={[WORKSHOP_VIDEOS.sattlerei]}
        testid="process-sattlerei"
      />

      {/* VORHER / NACHHER */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Vorher / Nachher: Ergebnisse aus unserer Sattlerei</h2>
          <p className="mt-3 text-slate-600">Ziehen Sie den Regler und vergleichen Sie selbst.</p>
          <div className="mt-7 grid md:grid-cols-2 gap-6">
            <div>
              <BeforeAfterSlider
                before="/images/gallery/himmel_before.jpg"
                after="/images/gallery/himmel_after.jpg"
                altBefore="Durchhängender Dachhimmel mit gelöstem Himmelstoff vor der Reparatur in der Autosattlerei Ludwigshafen"
                altAfter="Neu bezogener Autohimmel nach der Reparatur — straffer, sauber verklebter Himmelstoff von Servus Autoglas"
                accent="#249143"
                testid="himmel-before-after"
              />
              <p className="mt-3 text-sm font-semibold text-slate-700 text-center">Autohimmel neu bezogen</p>
            </div>
            <div>
              <BeforeAfterSlider
                before="/images/gallery/verdeck_before.jpg"
                after="/images/gallery/verdeck_after.jpg"
                altBefore="Cabrioverdeck mit gelöster Heckscheibe vor der Reparatur — die Scheibe löst sich vom Verdeckstoff"
                altAfter="Cabrio-Heckscheibe nach der fachgerechten Verklebung mit dem Verdeck — sauberes, dichtes Ergebnis"
                accent="#249143"
                testid="verdeck-before-after"
              />
              <p className="mt-3 text-sm font-semibold text-slate-700 text-center">Cabrio-Heckscheibe neu verklebt</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Häufige Fragen zur Autosattlerei</h2>
          <div className="mt-6 divide-y divide-slate-200 border border-slate-200 rounded-2xl overflow-hidden" data-testid="sattlerei-faq">
            {FAQS.map((f, i) => (
              <div key={f.q} className="bg-white">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50">
                  {f.q}
                  <CaretDown size={16} className={`flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <div className={openFaq === i ? "px-5 pb-4" : "hidden"}>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RATGEBER / WISSEN */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sattlerei-Wissen: Warum entstehen diese Schäden?</h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-3xl">
            Ein herunterhängender Dachhimmel, eine gelöste Cabrio-Heckscheibe oder verschlissene Materialien im
            Innenraum entstehen meist nicht plötzlich, sondern entwickeln sich über mehrere Jahre. Hitze,
            Sonneneinstrahlung, Feuchtigkeit, mechanische Belastungen und Materialermüdung spielen dabei eine
            wichtige Rolle. In unseren Ratgebern erklären wir verständlich, warum ein Schaden entsteht, welche
            Reparaturmöglichkeiten bestehen und wie Sie Ihre Materialien richtig pflegen.
          </p>
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sattlerei-ratgeber-links">
            {RATGEBER_LINKS.map(({ to, t, d, tag }) => (
              <Link
                key={to}
                to={to}
                className="group rounded-2xl border border-slate-200 hover:border-[#55C96B] transition-colors p-5 flex flex-col bg-slate-50 hover:bg-white"
                data-testid={`ratgeber-link-${to.split("/").pop()}`}
              >
                <span className="self-start px-2.5 py-1 rounded-full bg-[#EAF8ED] text-[#249143] text-[11px] font-bold uppercase tracking-wider">{tag}</span>
                <h3 className="mt-3 font-bold text-slate-900">{t}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed flex-1">{d}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#249143]">
                  Ratgeber lesen <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LOKAL / SEO */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sattlerei Ludwigshafen — Autohimmel, Cabrioverdeck &amp; mehr</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Unsere Autosattlerei in Ludwigshafen ist Ihre Anlaufstelle, wenn der Autohimmel hängt und neu bezogen
            werden soll, sich die Cabrio-Heckscheibe vom Verdeck löst und neu geklebt werden muss oder das
            Cabrioverdeck repariert bzw. erneuert werden soll. Sie erhalten für viele Arbeiten Ihren Preis direkt
            online und buchen Ihren Abgabetermin verbindlich — ohne Anfrage-Pingpong.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Typische Anliegen unserer Kunden: Der Dachhimmel hängt durch und der Himmelstoff soll neu bezogen werden,
            die Cabrio-Heckscheibe löst sich bei Hitze vom Verdeck, oder das Cabrioverdeck ist undicht. Für all das
            erhalten Sie bei uns feste, faire Preise statt vager Kostenvoranschläge — auch für Kunden aus Mannheim,
            Frankenthal, Speyer, Worms und Heidelberg lohnt sich der kurze Weg. Ebenfalls bei uns:{" "}
            <Link to="/scheibentoenung" className="text-[#249143] font-medium underline">Scheibentönung zum Festpreis ab 250 €</Link>{" "}
            und klassische <Link to="/leistungen" className="text-[#249143] font-medium underline">Autoglas-Reparaturen</Link>{" "}
            mit Termin innerhalb von 24 Stunden.
          </p>
          <p className="mt-4 text-sm text-slate-500">
            Auch in Ihrer Stadt:{" "}
            <Link to="/sattlerei-mannheim" className="text-[#249143] font-medium underline">Autosattlerei Mannheim</Link>{" · "}
            <Link to="/sattlerei-heidelberg" className="text-[#249143] font-medium underline">Autosattlerei Heidelberg</Link>{" · "}
            <Link to="/sattlerei-speyer" className="text-[#249143] font-medium underline">Autosattlerei Speyer</Link>
          </p>
          <div className="mt-5 flex items-center gap-2 text-slate-700">
            <MapPin size={20} weight="fill" className="text-[#249143]" />
            <span className="text-sm font-medium">Servus Autoglas · AutoGlas Erol GmbH · Frankenthaler Str. 98, 67059 Ludwigshafen am Rhein</span>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      {quote && step >= 3 && step < 6 && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white border-t border-[#cdeed6] p-3 flex items-center justify-between gap-3 z-40" data-testid="sattlerei-sticky-cta">
          <div>
            <div className="font-bold text-[#249143]">{quote.price ? `${priceStr} inkl. MwSt.` : `ab ${eur(quote.price_from)} €`}</div>
            <div className="text-[11px] text-slate-500">{make} {model} · min. 3 Werktage</div>
          </div>
          <Button onClick={() => (step === 3 ? loadAvailability() : window.scrollTo({ top: 0, behavior: "smooth" }))} className={`h-11 px-5 ${GREEN} text-white font-semibold rounded-xl`}>
            {step === 3 ? "Termin wählen" : "Weiter"}
          </Button>
        </div>
      )}
    </div>
  );
}
